import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { LoadingButton } from '@/components/LoadingButton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { OctagonAlert, ShieldAlert, Building2 } from 'lucide-react';
import { useRoles } from '@/contexts/RolesContext';
import { useOrgs } from '@/contexts/OrgsContext';
import { sanitizeHtml } from '@/utils/sanitizeHtml';
import type { ActiveNoticeResponse } from '@/api/auth/missionImpactAuthAPI.schemas';

interface AcknowledgeUrgentNoticeDialogProps {
  notice: ActiveNoticeResponse;
  onAcknowledge: (noticeId: string) => Promise<void>;
}

/**
 * Full-screen blocking dialog for urgent system notices
 * User must acknowledge to proceed to the app
 */
export function AcknowledgeUrgentNoticeDialog({
  notice,
  onAcknowledge,
}: AcknowledgeUrgentNoticeDialogProps) {
  const { isAdmin } = useRoles();
  const { organizations, selectedOrganizationId, setSelectedOrganizationId } = useOrgs();
  const [isAcknowledging, setIsAcknowledging] = useState(false);

  const isOrgLevel = notice.acknowledgementMode.toLowerCase() === 'org' ||
                     notice.acknowledgementMode.toLowerCase() === 'organization';
  const canAcknowledge = !isOrgLevel || isAdmin;

  console.log('[AcknowledgeUrgentNoticeDialog] Notice acknowledgement check:', {
    noticeId: notice.id,
    noticeTitle: notice.title,
    acknowledgementMode: notice.acknowledgementMode,
    isOrgLevel,
    isAdmin,
    canAcknowledge,
  });

  const handleOrgChange = (orgId: string) => {
    console.log('[AcknowledgeUrgentNoticeDialog] Switching organization:', {
      from: selectedOrganizationId,
      to: orgId,
    });
    setSelectedOrganizationId(orgId);
  };

  const handleAcknowledge = async () => {
    setIsAcknowledging(true);
    try {
      await onAcknowledge(notice.id);
    } finally {
      setIsAcknowledging(false);
    }
  };

  return (
    <Dialog open={true} modal>
      <DialogContent
        className="max-w-2xl"
        showCloseButton={false}
        onPointerDownOutside={(e) => { e.preventDefault(); }}
        onEscapeKeyDown={(e) => { e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <OctagonAlert className="h-5 w-5 text-destructive" />
            {notice.title}
          </DialogTitle>
          <DialogDescription>{notice.summary}</DialogDescription>
        </DialogHeader>

        <div
          className="text-sm [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-4 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mb-3 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mb-2 [&_h4]:text-base [&_h4]:font-semibold [&_h4]:mb-2 [&_p]:my-2 [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:ml-6 [&_ol]:my-2 [&_li]:my-1 [&_strong]:font-bold [&_b]:font-bold [&_em]:italic [&_i]:italic [&_a]:text-blue-600 [&_a]:underline [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm [&_code]:font-mono"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(notice.bodyHtml) }}
        />

        {isOrgLevel && !isAdmin && (
          <>
            <Alert variant="destructive">
              <ShieldAlert className="h-4 w-4" />
              <AlertDescription>
                An organization administrator is required to acknowledge this notice before continuing.
              </AlertDescription>
            </Alert>

            {organizations.length > 1 && (
              <>
                <Separator />
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Switch to another organization
                  </label>
                  <Select value={selectedOrganizationId ?? undefined} onValueChange={handleOrgChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select organization" />
                    </SelectTrigger>
                    <SelectContent>
                      {organizations.map((org) => (
                        <SelectItem key={org.orgId} value={org.orgId}>
                          {org.orgName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    If you are an administrator of another organization, switch to it to acknowledge this notice.
                  </p>
                </div>
              </>
            )}
          </>
        )}

        <DialogFooter>
          {canAcknowledge && (
            <LoadingButton onClick={() => { void handleAcknowledge(); }} isLoading={isAcknowledging}>
              {isOrgLevel ? 'Acknowledge for Organization' : 'Acknowledge and Continue'}
            </LoadingButton>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
